import { useEffect } from 'react';

interface PageSEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
}

export default function PageSEO({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage,
  articlePublishedTime,
  articleModifiedTime,
}: PageSEOProps) {
  useEffect(() => {
    if (typeof document === 'undefined') {
      return null;
    }

    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    const pageUrl = canonicalUrl || 'http://51cgwang.cn/';
    const canonicalEl = document.querySelector('link[rel="canonical"]');
    if (canonicalEl) canonicalEl.setAttribute('href', pageUrl);
    else {
      const link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', pageUrl);
      document.head.appendChild(link);
    }

    setMeta('og:title', ogTitle || title, true);
    setMeta('og:description', ogDescription || description, true);
    setMeta('og:url', pageUrl, true);
    setMeta('og:type', articlePublishedTime ? 'article' : 'website', true);
    if (ogImage) setMeta('og:image', ogImage, true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', ogTitle || title);
    setMeta('twitter:description', ogDescription || description);

    if (articlePublishedTime) {
      setMeta('article:published_time', articlePublishedTime, true);
    }
    if (articleModifiedTime) {
      setMeta('article:modified_time', articleModifiedTime, true);
    }

    return () => {
      const metaTags = document.head.querySelectorAll('meta');
      metaTags.forEach((tag) => {
        const name = tag.getAttribute('name') || tag.getAttribute('property');
        if (
          name === 'description' ||
          name === 'keywords' ||
          name === 'og:title' ||
          name === 'og:description' ||
          name === 'og:url' ||
          name === 'og:type' ||
          name === 'og:image' ||
          name === 'twitter:card' ||
          name === 'twitter:title' ||
          name === 'twitter:description' ||
          name === 'article:published_time' ||
          name === 'article:modified_time'
        ) {
          tag.remove();
        }
      });
    };
  }, [title, description, keywords, canonicalUrl, ogTitle, ogDescription, ogImage, articlePublishedTime, articleModifiedTime]);

  return null;
}